<template>
  <PackageExpansionsManagerUI
    v-loading="loading"
    :main-obb-files="mainObbFiles"
    :patch-obb-files="patchObbFiles"
    :attached-main-obb="attachedMainObb"
    :attached-patch-obb="attachedPatchObb"
    :main-obb-upload-path="mainObbUploadPath"
    :patch-obb-upload-path="patchObbUploadPath"
    :error-message="errorMessage"
    @change="attachObbToPackage"
    @detach="detachObb"
  />
</template>

<script>
import PackageExpansionsManagerUI from '@@/app/common/PackageExpansionsManagerUI';
import packageName from '@/mixins/packageName';
import { Obb } from '@/api/api';
import { mapActions, mapGetters } from 'vuex';

import {
  UNCOMMITTED_RELEASE,
  UNCOMMITTED_RELEASE_REPLACE_PACKAGE,
} from '@/store/types';

import {
  getMainObbUploadEndpoint,
  getPatchObbUploadEndpoint,
} from '@/api/app-obb/endpoints';

export default {
  name: 'NewReleasePackageExpansionsManager',
  components: { PackageExpansionsManagerUI },
  mixins: [packageName],

  props: {
    packageToken: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      mainObbFiles: [],
      patchObbFiles: [],
      errorMessage: '',
      loading: false,
    };
  },

  computed: {
    ...mapGetters({ release: UNCOMMITTED_RELEASE }),
    packageItem() {
      return this.release?.packages?.find(
        pkg => pkg.token === this.packageToken,
      );
    },
    attachedMainObb() {
      const isMainExpansion = ({ fileType }) => fileType === 'M';
      const file = this.packageItem?.additionalFiles?.find(isMainExpansion);
      return file?.id;
    },
    attachedPatchObb() {
      const isPatchExpansion = ({ fileType }) => fileType === 'P';
      const file = this.packageItem?.additionalFiles?.find(isPatchExpansion);
      return file?.id;
    },
    mainObbUploadPath() {
      return getMainObbUploadEndpoint(this.packageName);
    },
    patchObbUploadPath() {
      return getPatchObbUploadEndpoint({
        packageName: this.packageName,
        mainObbId: this.attachedMainObb,
      });
    },
  },

  watch: {
    attachedMainObb() {
      this.getPatchObbs();
    },
  },

  created() {
    this.getMainObbs();
    this.getPatchObbs();
  },

  methods: {
    ...mapActions({
      updateNewReleasePackage: UNCOMMITTED_RELEASE_REPLACE_PACKAGE,
    }),

    getMainObbs() {
      this.errorMessage = '';
      this.loading = true;

      return Obb.getMainObbs(this.packageName)
        .then(({ data }) => {
          this.mainObbFiles = data;
        })
        .catch(({ message }) => {
          this.errorMessage = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getPatchObbs() {
      if (!this.attachedMainObb) return;

      this.errorMessage = '';
      this.loading = true;

      return Obb.getPatchObbs({
        packageName: this.packageName,
        mainObbId: this.attachedMainObb,
      })
        .then(({ data }) => {
          this.patchObbFiles = data;
        })
        .catch(({ message }) => {
          this.errorMessage = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    attachObbToPackage(obbId) {
      this.errorMessage = '';
      this.loading = true;

      return Obb.attach({
        packageName: this.packageName,
        packageId: this.packageItem.token,
        obbId,
      })
        .then(({ message, data }) => {
          this.$message.success(message);
          this.updateNewReleasePackage(data);
        })
        .catch(({ message }) => {
          this.errorMessage = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    detachObb(obbId) {
      this.loading = true;

      return Obb.detach({
        packageName: this.packageName,
        packageId: this.packageItem.token,
        obbId,
      })
        .then(({ message, data }) => {
          this.$message.success(message);
          this.updateNewReleasePackage(data);
        })
        .catch(({ message }) => {
          this.errorMessage = message;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
